import React, { useCallback, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, isEqual, noop, omit } from 'underscore';
import classNames from 'classnames';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { List } from '@fluentui/react/lib/List';
import { FocusZone, FocusZoneDirection } from '@fluentui/react/lib/FocusZone';
import { SelectableItemView, PageNavigationFooter } from '@bingads-webui-campaign-react/smart-component-primitive';

const IMAGE_MARGIN_RIGHT = 12;
const IMAGE_MARGIN_BOTTOM = 12;

const getStyles = () => ({
  container: {
    position: 'relative',
  },
  item: {
    float: 'left',
    margin: `0 ${IMAGE_MARGIN_RIGHT}px ${IMAGE_MARGIN_BOTTOM}px 0`,
    padding: 0,
  },
  clearFloat: {
    clear: 'both',
  },
  navWrap: {
    display: 'flex',
    position: 'absolute',
    justifyContent: 'center',
    width: '100%',
    bottom: '14px',
  },
  noImageContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  tipsMsg: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pagerIcon: {
    padding: '0',
  },
  horizontalListView: {
    overflowX: 'auto',
    whiteSpace: 'nowrap',
    padding: '10px 5px',

    '& .ms-List-page, & .ms-List-cell': {
      display: 'inline-block',
    },
  },
});

export const UnStyledSelectImagesListView = ({
  items,
  classes,
  onRenderImageItem,
  selectable,
  onItemSelectionChanged,
  onSelectionChanged,
  imageWidth,
  imageHeight,
  pageHeight,
  i18n,
  isMultiple,
  noImageTips,
  showPagination,
  itemsPerPage,
  horizontal,
  itemToUnselect,
  resetItemToUnselect,
}) => {
  const columnCount = useRef(0);
  const [viewItems, setViewItems] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const offsetIndex = pageIndex * itemsPerPage;

  const handleOnItemSelectionChanged = useCallback((item, index) => {
    let newItems = [...viewItems];

    if (!isMultiple) {
      newItems = newItems.map(it => ({ ...it, selected: false }));
      newItems[index + offsetIndex].selected = true;
    } else {
      newItems[index + offsetIndex].selected = !newItems[index + offsetIndex].selected;
    }

    setViewItems(newItems);
    onSelectionChanged(newItems);
    onItemSelectionChanged(item);
  }, [viewItems, isMultiple, onItemSelectionChanged, offsetIndex, onSelectionChanged]);

  const onRenderCell = useCallback(
    (item, index) => {
      const { selected, isDisabled } = item;

      return (
        <SelectableItemView
          item={item}
          selectable={selectable}
          disabled={isDisabled}
          selected={selected}
          onChange={() => { handleOnItemSelectionChanged(item, index); }}
          className={classes.item}
          width={imageWidth}
          height={imageHeight}
          onRenderItem={() => onRenderImageItem(item, index)}
          ariaLabel={i18n.getString(_TL_('Image {{number}}'), { number: index + 1 })}
          isMultiple={isMultiple}
        />
      );
    },
    [
      imageWidth,
      imageHeight,
      classes.item,
      onRenderImageItem,
      selectable,
      handleOnItemSelectionChanged,
      i18n,
      isMultiple,
    ]
  );

  useEffect(() => {
    setViewItems([...items]);
  }, [items]);

  useEffect(() => {
    /**
     * Pass by parent, when it present, this component will try to unselect it from its list
     * If it found it, then will unselect it an tell parent it has been unselected
     */
    if (itemToUnselect) {
      const targetIndex = viewItems.findIndex(item => isEqual(omit(itemToUnselect, 'selected'), omit(item, 'selected')));
      if (targetIndex > -1) {
        const newViewItems = [...viewItems];
        newViewItems[targetIndex].selected = false;
        setViewItems(newViewItems);
        // Notify parent that this item has been unselected successfully
        resetItemToUnselect(itemToUnselect);
      }
    }
  }, [itemToUnselect, resetItemToUnselect, viewItems]);

  const getPageHeight = useCallback(() => pageHeight, [pageHeight]);
  const getItemCountForPage = useCallback((itemIndex, surfaceRect) => {
    if (itemIndex === 0) {
      columnCount.current = Math.ceil(surfaceRect.width / (imageWidth + IMAGE_MARGIN_RIGHT));
    }
    return Math.ceil(pageHeight / (imageHeight + IMAGE_MARGIN_BOTTOM)) * columnCount;
  }, [pageHeight, imageWidth, imageHeight]);

  return (
    <FocusZone className="smart-page-select-image-list-view" direction={FocusZoneDirection.vertical}>
      <div
        className={classNames(
          classes.container,
          isEmpty(viewItems) ? classes.noImageContainer : null
        )}
        style={showPagination ? { height: pageHeight } : {}}
      >
        {
          viewItems.length > 0 ?
            <List
              className={horizontal ? classes.horizontalListView : null}
              items={
                showPagination ?
                  viewItems.slice(offsetIndex, offsetIndex + itemsPerPage)
                  : viewItems
              }
              getPageHeight={getPageHeight}
              getItemCountForPage={getItemCountForPage}
              onRenderCell={onRenderCell}
              ignoreScrollingState
            />
            :
            <div className={classes.tipsMsg}>
              {i18n.getString(noImageTips)}
            </div>
        }
        <div className={classes.clearFloat} />
        {
          showPagination && viewItems.length > 0 ?
            <div className={classes.navWrap}>
              <PageNavigationFooter
                pageIndex={pageIndex}
                totalPageNumber={Math.ceil(items.length / itemsPerPage)}
                onNextPageClick={() => setPageIndex(pageIndex + 1)}
                onPreviousPageClick={() => setPageIndex(pageIndex - 1)}
                onRandomPageSelect={index => setPageIndex(index)}
                classes={classes}
                i18n={i18n}
              />
            </div>
            : null
        }
      </div>
    </FocusZone>
  );
};

UnStyledSelectImagesListView.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  items: PropTypes.arrayOf(PropTypes.any),
  imageWidth: PropTypes.number.isRequired,
  imageHeight: PropTypes.number.isRequired,
  pageHeight: PropTypes.number.isRequired,
  onRenderImageItem: PropTypes.func.isRequired,
  onItemSelectionChanged: PropTypes.func,
  onSelectionChanged: PropTypes.func,
  selectable: PropTypes.bool,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  isMultiple: PropTypes.bool,
  noImageTips: PropTypes.string,
  showPagination: PropTypes.bool,
  itemsPerPage: PropTypes.number,
  horizontal: PropTypes.bool,
  itemToUnselect: PropTypes.shape({}),
  resetItemToUnselect: PropTypes.func,
};

UnStyledSelectImagesListView.defaultProps = {
  items: [],
  selectable: false,
  onItemSelectionChanged: noop,
  onSelectionChanged: noop,
  isMultiple: false,
  noImageTips: _TL_('This is where your images will appear.'),
  showPagination: false,
  itemsPerPage: 6,
  horizontal: false,
  itemToUnselect: null,
  resetItemToUnselect: noop,
};

export const SelectImagesListView = withDefaultStyles(
  UnStyledSelectImagesListView,
  getStyles
);
